// Intercom service for chat/support integration
// Ported from master-client/src/app/app.component.ts Intercom methods
import { Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { UtilsService } from './utils.service';
import { UserService } from './user.service';
import { environment } from '../environments/environment';

// Intercom app ID - environment-specific (test workspace for dev/staging, production for prod)
const INTERCOM_APP_ID = environment.intercomAppId;

// Type declaration for Intercom global object
declare global {
  interface Window {
    Intercom: any;
    intercomSettings: any;
  }
}

// Interface for Intercom user data
// Reason: company fields do not belong here. Intercom ignores company data sent through the
// Messenger whenever a JWT is present, so the company is signed into the JWT by the server
export interface IntercomUserData {
  name: string;
  email: string;
  user_id: string;
  intercom_user_jwt?: string;
  is_admin?: boolean;
  is_owner?: boolean;
  support_hidden?: string;
}

@Injectable({
  providedIn: 'root'
})
export class IntercomService {

  // Track initialization state
  private initialized: boolean = false;
  private scriptLoaded: boolean = false;
  private modalOpen: boolean = false;

  constructor(
    private utils: UtilsService,
    private user: UserService,
    private platform: Platform,
    private ngZone: NgZone
  ) {
    // Subscribe to intercom state changes from utils (used by modals to hide/show)
    this.utils.intercomState.subscribe((state: string) => {
      if (state === 'hide') {
        this.modalOpen = true;
        this.hide();
      } else if (state === 'show') {
        this.modalOpen = false;
        this.show();
      }
    });
  }

  // Load the Intercom JavaScript SDK (web only)
  public loadScript(): void {
    if (this.scriptLoaded || Capacitor.isNativePlatform() || this.utils.intercomDisable) {
      return;
    }

    this.platform.ready().then(() => {
      // Set intercomSettings with app_id before loading the snippet so the widget
      // has the app_id immediately when it loads, even before boot() is called
      window.intercomSettings = { app_id: INTERCOM_APP_ID };

      const script = document.createElement('script');
      // Intercom snippet from old codebase app.component.ts lines 303-309
      const content = `(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/${INTERCOM_APP_ID}';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;
      script.text = content;
      script.type = 'text/javascript';
      script.async = true;
      script.charset = 'utf-8';
      document.getElementsByTagName('head')[0].appendChild(script);
      this.scriptLoaded = true;
      this.utils.logDebug('Intercom script loaded');
    });
  }

  // Initialize Intercom with JWT-based authentication
  // User data (name, email, user_id) is embedded in the JWT signed by the server
  public init(intercomTokens: any, isSupportHidden: boolean): void {
    if (this.utils.intercomDisable || this.initialized || this.utils.offline || !this.user.id) {
      return;
    }

    let logEvent: string;
    let userToken: string;

    // Determine platform and get appropriate JWT
    // Must use Capacitor.isNativePlatform() to distinguish native apps from mobile browsers,
    // because platform.is('ios'/'android') returns true in mobile browsers too, but the
    // web JS SDK requires the web token (Intercom has separate secrets per platform)
    if (Capacitor.isNativePlatform() && this.platform.is('android')) {
      logEvent = 'android-app';
      userToken = intercomTokens?.android || '';
    } else if (Capacitor.isNativePlatform() && this.platform.is('ios')) {
      logEvent = 'ios-app';
      userToken = intercomTokens?.ios || '';
    } else {
      logEvent = 'web-app';
      userToken = intercomTokens?.web || '';
    }

    this.utils.logDebug('initializing intercom for ' + this.user.email);

    if (Capacitor.isNativePlatform()) {
      // Native platform - use Capacitor plugin when available
      // TODO: Implement native Intercom support when needed
      this.utils.logDebug('Intercom native support not yet implemented');
    } else {
      // Web platform - use JavaScript SDK with JWT authentication
      // JWT authenticates the user; additional attributes are passed alongside
      // Reason: do not add company data to this payload. Intercom ignores company data sent through
      // the Messenger whenever a JWT is present; the company is signed into the JWT by the server
      // (see master-server/app/services/intercom.ts getIntercomTokens)
      const data: any = {
        api_base: 'https://api-iam.intercom.io',
        app_id: INTERCOM_APP_ID,
        intercom_user_jwt: userToken,
        name: this.user.displayName,
        email: this.user.email,
        user_id: this.user.id
      };

      if (window.Intercom) {
        window.Intercom('boot', data);
        window.Intercom('trackEvent', logEvent);

        if (isSupportHidden) {
          window.Intercom('update', {
            'hide_default_launcher': true
          });
        }
      } else {
        this.utils.logError('Intercom SDK not loaded');
      }
    }

    this.utils.intercomState.next('initialized');
    this.initialized = true;
  }

  // Reason: this service controls the widget only - boot, launcher visibility, events, shutdown.
  // Do not add attribute writes here: the Messenger silently discards company data whenever a JWT is
  // present, and silently drops support_hidden under JWT auth, so they would appear to work while
  // landing nowhere. Attributes go through the server, via team-data.service updateIntercom()

  // Hide the Intercom launcher
  // Ported from master-client/src/app/app.component.ts hideIntercom() lines 646-663
  public hide(): void {
    if (this.utils.intercomDisable || !this.initialized) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      // TODO: Implement native hide when needed
      this.utils.logDebug('Intercom native hide not yet implemented');
    } else if (window.Intercom) {
      window.Intercom('update', {
        'hide_default_launcher': true
      });
    }
  }

  // Show the Intercom launcher
  // Ported from master-client/src/app/app.component.ts showIntercom() lines 666-683
  public show(isSupportHidden: boolean = false): void {
    if (this.utils.intercomDisable || !this.initialized || this.utils.offline || !this.user.id || isSupportHidden) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      // TODO: Implement native show when needed
      this.utils.logDebug('Intercom native show not yet implemented');
    } else if (window.Intercom) {
      window.Intercom('update', {
        'hide_default_launcher': false
      });
    }
  }

  // Set visibility based on support hidden setting
  // Ported from master-client/src/app/app.component.ts setIntercomVisibility() lines 686-691
  public setVisibility(isSupportHidden: boolean): void {
    if (isSupportHidden) {
      this.hide();
    } else if (!this.modalOpen) {
      this.show();
    }
  }

  // Open the chat/support window
  // Ported from master-client/src/app/app.component.ts openChat() lines 695-704
  public openChat(isSupportHidden: boolean = false): void {
    if (this.utils.intercomDisable || !this.initialized || this.utils.offline || !this.user.id || isSupportHidden) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      // TODO: Implement native chat when needed
      this.utils.logDebug('Intercom native chat not yet implemented');
    } else if (window.Intercom) {
      window.Intercom('showNewMessage');
    }
  }

  // Track an event in Intercom
  public trackEvent(eventName: string, metadata?: any): void {
    if (this.utils.intercomDisable || !this.initialized || this.utils.offline || !this.user.id) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      // TODO: Implement native event tracking when needed
    } else if (window.Intercom) {
      if (metadata) {
        window.Intercom('trackEvent', eventName, metadata);
      } else {
        window.Intercom('trackEvent', eventName);
      }
    }
  }

  // Shutdown Intercom (called on logout)
  // Ported from master-client/src/app/app.component.ts user.loggedOut subscription lines 418-429
  public shutdown(): void {
    if (this.utils.intercomDisable) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      // TODO: Implement native shutdown when needed
      this.utils.logDebug('Intercom native shutdown not yet implemented');
    } else if (window.Intercom) {
      window.Intercom('shutdown');
    }

    this.utils.intercomState.next('shutdown');
    this.initialized = false;
  }

  // Check if Intercom is initialized
  public isInitialized(): boolean {
    return this.initialized;
  }
}
